import React, { FC } from 'react';
import cn from 'clsx';
import Link, { LinkProps } from '@v2/shared/components/Link';

/**
 * `LinkProps` already carries href, className, target, referrerPolicy, onClick and
 * children, so `emphasised` is the only prop this component adds. Not exported: nothing
 * imports it since FooterIconLink stopped building on this component.
 */
type Props = LinkProps & {
  /**
   * Draws the link white and semibold, for the one entry in a column that is the
   * column's own destination — All Categories, Help Center, Contact Us.
   *
   * A flag rather than a className the caller hands in, because the two states set the
   * same two properties. `cn` is clsx and settles nothing, so a `text-white` passed in
   * beside the default `text-slate-400` would leave both on the element and let the
   * stylesheet's own order pick the winner.
   */
  emphasised?: boolean;
  children?: React.ReactNode;
};

const FooterLink: FC<Props> = ({ href, className, emphasised = false, children, ...props }) => {
  return (
    <Link
      href={href}
      className={cn(
        // A 44px row wherever a finger is doing the aiming, dropped above lg where a
        // pointer is. `flex` and `lg:block` both set `display`, which is one of the
        // properties postcss/beatLegacyImportant.js raises to !important inside the V2
        // scope — so the two are settled on source order, and Tailwind emits the variant
        // last. `min-h-[44px]` against `lg:min-h-0` is not raised and is settled the same
        // way. The `lg:py-1.5` is the mockup's 12px gap between rows, half on each.
        'flex min-h-[44px] items-center hover:underline lg:block lg:min-h-0 lg:py-1.5',
        emphasised ? 'font-semibold text-white' : 'font-medium text-slate-400 active:text-slate-300',
        className
      )}
      {...props}
    >
      {children}
    </Link>
  );
};

export default FooterLink;
